/**
 * Every image a public page renders is fetched through our own `/image-proxy`.
 * Nothing off-origin is ever handed to a viewer's browser. Issue #238.
 *
 * ## The reason is the viewer, not the bytes
 *
 * A creator page is composed entirely of material harvested from somewhere
 * else — `mc_session_components.image_url` holds raw remote URLs (arxiv,
 * Nature, a Sanity CDN, a CloudFront path that turns out to be someone else's
 * avatar). An `<img src>` pointing at any of them makes every visitor's
 * browser issue a request to that host, disclosing their IP and User-Agent.
 * On the surface this product wants people pasting into group chats, that is a
 * list of third parties learning who is reading whom.
 *
 * `services/component_extractors.py` in the backend states it for its own
 * surface: *"a third-party iframe leaks the viewer's IP, the exact thing
 * `/image-proxy` exists to prevent for card images."* This is the same rule,
 * applied at read time on the client so it does not depend on every producer
 * remembering it. Caching and hotlink rot are real, and secondary.
 *
 * ## The route this builds a URL for
 *
 * `GET /image-proxy?u=…` — `api/routes/clips.py`, unauthenticated because clip
 * pages are public, rate limited `120/minute` per client address, capped at
 * 5MB, raster content-types only, and guarded against SSRF by
 * `services/image_proxy.py`, which fetches a **pinned resolved address** rather
 * than re-resolving the hostname. The parameter is `u`; it is read off the
 * route signature (`async def image_proxy(request: Request, u: str)`), not
 * assumed.
 *
 * ## Two things it deliberately does not do
 *
 * - **Same-origin is a no-op.** `/spotlight/{slug}/og-image.png` and
 *   `/spotlight/{slug}/appclip-code.svg` are rendered by us; proxying our own
 *   assets adds a hop for nothing, and the App Clip code is an SVG, which the
 *   proxy's raster allowlist would refuse outright. This also makes the
 *   function idempotent: an already-proxied URL is on the API origin, so
 *   passing it through twice returns it unchanged.
 * - **Plain http is dropped, not hotlinked.** `services/image_proxy.py`
 *   refuses any scheme but https, so an `http://` source has no proxied form —
 *   and a public https page cannot load it as mixed content anyway. Returning
 *   the raw URL "so something renders" would be exactly the leak this exists to
 *   prevent. Matches `_proxied` in the backend's `services/session_clip.py`.
 *
 * The mirror of this module for the server-rendered pages is
 * `api/_lib/imageProxy.ts` — same rule, different env plumbing (`api/` is a
 * separate tsconfig project and cannot read `import.meta.env`). Neither is
 * what holds the line: `src/test/creator-page-images-proxied.test.tsx`
 * asserts the *output* of both layers, so a surface that bypasses both still
 * fails.
 */

const API_BASE = (
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  "https://api.manticscore.com"
).replace(/\/$/, "");

/** Read off the route signature in `api/routes/clips.py`, not guessed. */
export const IMAGE_PROXY_PARAM = "u";

export const IMAGE_PROXY_PATH = "/image-proxy";

function apiOrigin(): string {
  try {
    return new URL(API_BASE).origin;
  } catch {
    return "";
  }
}

/**
 * Is this a URL we serve ourselves, and therefore already safe to emit?
 *
 * Root-relative counts: whatever origin the page is on is ours, including a
 * Vercel preview host, which is why this is not a hardcoded host list alone.
 * An absolute URL has to be the configured API origin (whatever scheme it is
 * configured with, so a local API base still works) or an https
 * manticscore.com host — matched on the whole label, so `evilmanticscore.com`
 * does not slip through a suffix test.
 */
export function isOwnImageURL(url: unknown): url is string {
  if (typeof url !== "string") return false;
  const raw = url.trim();
  if (!raw) return false;
  // Protocol-relative is off-origin and has no scheme to proxy with.
  if (raw.startsWith("//")) return false;
  if (raw.startsWith("/")) return true;
  try {
    const parsed = new URL(raw);
    if (parsed.origin === apiOrigin()) return true;
    if (parsed.protocol !== "https:") return false;
    return (
      parsed.hostname === "manticscore.com" || parsed.hostname.endsWith(".manticscore.com")
    );
  } catch {
    return false;
  }
}

/**
 * The URL to actually put in an `src`, or null when there is nothing safe to
 * render.
 *
 * Null is a real answer and callers must handle it by dropping the image. It
 * is never a reason to fall back to the raw URL.
 */
export function proxiedImageURL(url: unknown): string | null {
  if (typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw) return null;
  if (isOwnImageURL(raw)) return raw;
  if (!/^https:\/\//i.test(raw)) return null;
  return `${API_BASE}${IMAGE_PROXY_PATH}?${IMAGE_PROXY_PARAM}=${encodeURIComponent(raw)}`;
}
